import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import store from '@/stores/store';
const { getStorageKey, loadFiltersFromStorage, saveFiltersToStorage } = utils;

const DEFAULT_FILTERS = { offset: 0, limit: 40 };

const createState = (options = {}) => ({
    products: [],
    // The initial endpoint URL for products.
    products_url: '/api/products/',
    // Separate field for storing the next page URL.
    next_products_url: null,
    products_filters: DEFAULT_FILTERS,
    products_count: 0,
    products_loaded: false,
    products_loading: false,
    products_loading_errors: null,
    products_stats: {},
    products_stats_loading: false,
    products_stats_loading_errors: null,
    products_stats_cancel_source: null,
    fetch_stats: false,
    cancel_source: null,
    _storageOptions: options || {},
});

const mutations = {
    resetProducts(state) {
        state.products = [];
        state.products_count = 0;
        state.products_loaded = false;
        state.products_loading = false;
        state.products_loading_errors = null;
        state.next_products_url = null;
        state.products_stats = {};
        state.products_stats_loading = false;
        state.products_stats_loading_errors = null;
    },
    updateProducts(state, products) {
        state.products = products;
        state.products_loaded = true;
        state.products_loading = false;
        state.products_loading_errors = null;
    },
    appendProducts(state, products) {
        state.products = state.products.concat(products);
    },
    updateProductsCount(state, products_count) {
        state.products_count = products_count;
    },
    updateProductsUrl(state, products_url) {
        state.products_url = products_url;
    },
    updateNextProductsUrl(state, nextUrl) {
        state.next_products_url = nextUrl;
    },
    updateProductsFilters(state, products_filters) {
        state.products_filters = products_filters;
        const { storageKey, fieldsToSave } = state._storageOptions;
        if (storageKey) {
            saveFiltersToStorage(storageKey, state.products_filters, fieldsToSave);
        }
    },
    updateStorageOptions(state, storage_options) {
        state._storageOptions = storage_options;
    },
    updateProductsLoading(state, products_loading) {
        state.products_loading = products_loading;
    },
    updateProductsLoadingErrors(state, products_loading_errors) {
        state.products_loading_errors = products_loading_errors;
    },
    setCancelSource(state, cancel_source) {
        state.cancel_source = cancel_source;
    },
    updateProductsStats(state, products_stats) {
        state.products_stats = products_stats;
    },
    updateProductsStatsLoading(state, loading) {
        state.products_stats_loading = loading;
    },
    updateProductsStatsLoadingErrors(state, errors) {
        state.products_stats_loading_errors = errors;
    },
    updateProductsStatsCancelSource(state, source) {
        state.products_stats_cancel_source = source;
    },
    updateFetchStats(state, fetch_stats) {
        state.fetch_stats = fetch_stats;
    },
};

const actions = {
    async fetchProducts({ commit, dispatch, state }) {
        if (
            store.getters['session/current_user_permissions'].indexOf("core.view_product") === -1
        ) {
            return;
        }

        if (state.cancel_source) {
            state.cancel_source.cancel('Operation canceled due to new request.');
        }

        const cancel_source = axios.CancelToken.source();
        commit('setCancelSource', cancel_source);

        commit('updateProductsLoading', true);
        commit('updateProductsLoadingErrors', null);
        if (state.fetch_stats) {
            commit('updateProductsStatsLoading', true);
        }

        try {
            // Call the initial endpoint with filters
            const response = await axios.get(state.products_url, {
                params: state.products_filters,
                cancelToken: cancel_source.token,
            });
            commit('updateProducts', response.data.results);
            commit('updateProductsCount', response.data.count);
            // Save the next URL separately
            commit('updateNextProductsUrl', response.data.next);
            dispatch('fetchProductsStats');
        } catch (xhr_error) {
            if (!axios.isCancel(xhr_error)) {
                const error = utils.handleError(xhr_error);
                commit('updateProductsLoadingErrors', error.details);
            }
        } finally {
            if (state.cancel_source === cancel_source) {
                commit('updateProductsLoading', false);
                commit('setCancelSource', null);
            }
        }
    },

    async fetchMoreProducts({ commit, state }) {
        // If there is no next URL, then we've reached the end.
        if (!state.next_products_url) {
            return;
        }

        if (state.cancel_source) {
            state.cancel_source.cancel('Operation canceled due to new request.');
        }

        const cancel_source = axios.CancelToken.source();
        commit('setCancelSource', cancel_source);

        commit('updateProductsLoading', true);
        commit('updateProductsLoadingErrors', null);

        try {
            // Use the next_products_url from state (it already contains GET parameters)
            const response = await axios.get(state.next_products_url, {
                cancelToken: cancel_source.token,
            });
            commit('appendProducts', response.data.results);
            // Update the next_products_url (could be null if no further pages)
            commit('updateNextProductsUrl', response.data.next);
        } catch (xhr_error) {
            if (!axios.isCancel(xhr_error)) {
                const error = utils.handleError(xhr_error);
                commit('updateProductsLoadingErrors', error.details);
            }
        } finally {
            if (state.cancel_source === cancel_source) {
                commit('updateProductsLoading', false);
                commit('setCancelSource', null);
            }
        }
    },

    async fetchProductsStats({ commit, state }) {
        if (
            !state.fetch_stats ||
            store.getters['session/current_user_permissions'].indexOf("core.view_product") === -1
        ) {
            commit('updateProductsStatsLoading', false);
            return;
        }

        if (state.products_stats_cancel_source) {
            state.products_stats_cancel_source.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateProductsStatsCancelSource', cancelSource);
        commit('updateProductsStatsLoading', true);
        commit('updateProductsStatsLoadingErrors', null);

        const statsUrl = `${state.products_url.replace(/\/?$/, "/")}stats/`;

        try {
            const response = await axios.get(statsUrl, {
                params: state.products_filters,
                cancelToken: cancelSource.token,
            });
            commit('updateProductsStats', response.data);
            commit('updateProductsStatsCancelSource', null);
            return response.data;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateProductsStatsLoadingErrors', error.details);
            throw error;
        } finally {
            commit('updateProductsStatsLoading', false);
        }
    },

    async init({ commit, state }, params) {
        commit('resetProducts');
        commit('updateFetchStats', params?.fetch_stats === undefined ? false : params.fetch_stats);
        commit('updateProductsStats', {});
        commit('updateProductsStatsLoading', false);
        
        // Update storage options if provided
        if (params?.storageOptions !== undefined) {
            await commit('updateStorageOptions', params.storageOptions);
        }
        
        // Load filters from localStorage if storage is configured
        const default_filters = { offset: 0, limit: 20, ordering: 'upc' };
        await commit('updateProductsFilters', { 
            ...default_filters, 
            ...(state._storageOptions?.storageKey ? loadFiltersFromStorage(state._storageOptions?.storageKey, {}) : {}),
            ...(params?.filters || {})
        });
        
        if (params && params.url) {
            commit('updateProductsUrl', params.url);
        }
        // Reset the next URL.
        commit('updateNextProductsUrl', null);
    },
};

export default (options = {}) => ({
    namespaced: true,
    state: createState(options),
    mutations,
    actions,
});
